// markdown-it pipeline for the mindex SPA reader.
//
// Public surface (exposed both as named exports for tests and as
// `window.mdPipeline` for the SPA runtime):
//   renderMarkdownDoc(md, scheme?) → { html, frontmatter, headings }
//   getShikiTheme(scheme) → string
//
// Tier 1 spec: docs/superpowers/specs/2026-05-01-obsidian-tier1-renderer-design.md
// Locked decisions: docs/features/obsidian-inspired-reader/roadmap.md §"Locked decisions — 2026-05-01"
//
// Shiki is loaded lazily and same-origin — the first highlight call
// dynamic-imports `/shiki-runtime.js` (engine + ~20 common grammars),
// and once it resolves later renders use the real highlighter. Until
// then, code blocks render as plain `<pre><code>…</code></pre>` with a
// `language-<lang>` class so the post-processor still picks the
// language label up. Non-bundled grammars are fetched per-language
// from `/shiki-langs/<id>.js`. No third-party CDN at runtime.

import yaml from 'js-yaml';

import { buildReaderMd } from '@mindex/core/markdown';
import { wrapHeadingsForFold, wrapCodeBlocksForCopy } from './post-processors.js';
import { getShikiTheme, resolveLang } from './scheme-themes.js';

// ---------- Shiki (lazy, same-origin) ----------
//
// Shiki is split into chunks served from this worker:
//
//   /shiki-runtime.js     — engine + ~20 common grammars + themes
//   /shiki-langs/<id>.js  — one per non-bundled grammar
//
// The runtime chunk is dynamic-imported on the first highlight() call.
// Until it loads, code blocks render as plain
// `<pre><code class="language-X">…</code></pre>` and the post-processor
// still picks the language label up. When the runtime resolves it pins
// itself on `window.mdShiki` and fires `mindex.shiki-ready`; the SPA
// listens and re-renders the active doc.
//
// Per-language lazy chunks are fetched on demand by the runtime
// (mdShiki.loadLazyLang) when a fence label resolves to
// {kind: 'lazy', id}. We track which lazy ids we've already requested
// to avoid re-fetching, and re-fire `mindex.shiki-ready` on success
// so the doc repaints with the now-loaded grammar.

let runtimePromise = null;
let runtimeReady = false;
let runtimeUnavailable = false;
let runtimeReadyFired = false;

const lazyLangPromises = new Map(); // id → Promise<boolean>
const lazyLangFailed = new Set();   // ids known to be missing/broken

function fireShikiReady() {
  if (runtimeReadyFired || typeof window === 'undefined') return;
  runtimeReadyFired = true;
  try {
    window.dispatchEvent(new CustomEvent('mindex.shiki-ready'));
  } catch (e) { /* no-op */ }
}

// We re-fire mindex.shiki-ready after a lazy lang resolves so a doc
// containing only lazy-grammar code blocks still gets a repaint.
function fireShikiRepaint() {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('mindex.shiki-ready'));
  } catch (e) { /* no-op */ }
}

// Dynamic import of the same-origin runtime chunk. The URL is
// constructed at runtime (non-literal) so esbuild does NOT try to
// follow it back into the markdown-pipeline bundle. The chunk is an
// ESM module; modern browsers honour `import()` regardless of the
// parent <script>'s type attribute.
function ensureShikiRuntime() {
  if (runtimeReady || runtimeUnavailable) return;
  if (runtimePromise) return;
  runtimePromise = (async () => {
    try {
      const url = '/shiki-runtime.js';
      // The shiki-runtime entry pins itself on `window.mdShiki` and
      // fires its own `mindex.shiki-ready` event — but we double-check
      // by reading the global so the rest of this module can stay
      // synchronous.
      await import(/* @vite-ignore */ url);
      if (typeof window === 'undefined' || !window.mdShiki) {
        throw new Error('shiki-runtime did not pin window.mdShiki');
      }
      runtimeReady = true;
      fireShikiReady();
    } catch (e) {
      runtimeUnavailable = true;
      // Leave runtime null; renders use plain fallback.
    }
  })();
}

// Lazy-load a non-bundled grammar from /shiki-langs/<id>.js. Returns
// the same boolean Promise across concurrent calls for the same id.
function ensureLazyLang(id) {
  if (lazyLangFailed.has(id)) return Promise.resolve(false);
  if (lazyLangPromises.has(id)) return lazyLangPromises.get(id);
  const p = (async () => {
    if (!runtimeReady) return false;
    if (typeof window === 'undefined' || !window.mdShiki) return false;
    try {
      const ok = await window.mdShiki.loadLazyLang(id);
      if (!ok) {
        lazyLangFailed.add(id);
        return false;
      }
      fireShikiRepaint();
      return true;
    } catch (e) {
      lazyLangFailed.add(id);
      return false;
    }
  })();
  lazyLangPromises.set(id, p);
  return p;
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function plainFallback(code, langKey) {
  const langClass = langKey ? ` class="language-${escapeHtml(langKey)}"` : '';
  return `<pre><code${langClass}>${escapeHtml(code)}</code></pre>`;
}

function highlight(code, lang, theme) {
  const langKey = (lang || '').toLowerCase();
  const resolved = resolveLang(langKey);

  // Always kick the runtime chunk request off on the first highlight.
  // The first render synchronously falls back to plain <pre><code>;
  // once the runtime resolves a `mindex.shiki-ready` event triggers a
  // re-render that picks up highlighted output.
  ensureShikiRuntime();

  // Lang isn't supported at all — plain <pre><code> with the original
  // (lowercased) label so the post-processor's lang chip is still right.
  if (!resolved) return plainFallback(code, langKey);

  // Runtime hasn't loaded yet — plain fallback now, repaint on
  // `mindex.shiki-ready`.
  if (!runtimeReady || !window.mdShiki) {
    if (resolved.kind === 'lazy') {
      // Defer the lazy fetch until after the runtime is up.
      // We re-fire `ensureLazyLang(id)` from the SPA's
      // mindex.shiki-ready listener path implicitly because the next
      // render call will hit this branch again with runtimeReady=true.
    }
    return plainFallback(code, langKey);
  }

  // For lazy langs, kick off the lang fetch but render plain now;
  // a `mindex.shiki-ready` will fire once the lang resolves.
  if (resolved.kind === 'lazy') {
    const loaded = window.mdShiki.getLoadedLanguages?.() || [];
    if (!loaded.includes(resolved.id)) {
      ensureLazyLang(resolved.id);
      return plainFallback(code, langKey);
    }
  }

  try {
    let html = window.mdShiki.codeToHtml(code, {
      lang: resolved.id,
      theme,
      // Drop the theme's `background-color` from the wrapping <pre>'s
      // inline style so the `.code-block` wrapper's tinted background
      // shows through. Token foreground colors on per-span styles are
      // preserved.
      transformers: [{
        pre(node) {
          const style = node.properties?.style;
          if (typeof style !== 'string') return;
          const next = style.replace(/background-color:[^;]+;?/g, '').trim();
          if (next) node.properties.style = next;
          else delete node.properties.style;
        },
      }],
    });
    // Shiki v1 doesn't emit `data-language` on the wrapping <pre>.
    // The post-processor reads that to populate the meta-row label;
    // inject the original fence label here so the lang chip stays
    // accurate for both bundled and lazy grammars (we want `dockerfile`
    // not `docker` in the chip if the user typed `dockerfile`).
    if (langKey && !/data-language=/.test(html)) {
      html = html.replace(/^(<pre[^>]*)>/, `$1 data-language="${escapeHtml(langKey)}">`);
    }
    return html;
  } catch (e) {
    return plainFallback(code, langKey);
  }
}

// ---------- Pipeline factory ----------
//
// Thin wrapper over @mindex/core's buildReaderMd. The factory owns the
// plugin chain (frontmatter / anchor / callouts / wikilink / task-lists /
// deflist / footnote / mark / emoji); the SPA passes its Shiki-driven
// highlighter and asks for permalinks. Wikilinks render as literal text
// in the SPA today (`mode: 'plain'`) — toggling that on is a future
// decision.

function buildMd(captureFrontmatter, theme) {
  return buildReaderMd({
    onFrontmatter: captureFrontmatter,
    highlight: (code, lang) => highlight(code, lang, theme),
    permalink: true,
    wikilink: 'plain',
    sourceLines: true,
  });
}

// ---------- Headings extraction ----------

function extractHeadings(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length; i++) {
    const tk = tokens[i];
    if (tk.type !== 'heading_open') continue;
    const level = +tk.tag.slice(1);
    const id = tk.attrGet('id') || '';
    const inline = tokens[i + 1];
    const text = inline && inline.type === 'inline' ? inline.content : '';
    out.push({ id, text, level });
  }
  return out;
}

// ---------- Public API ----------

export function renderMarkdownDoc(source, scheme) {
  const theme = getShikiTheme(scheme);

  let frontmatterRaw = null;
  const captureFrontmatter = (raw) => { frontmatterRaw = raw; };

  // Build a fresh md instance per render so the slug-collision counter
  // resets per document.
  const md = buildMd(captureFrontmatter, theme);

  const env = {};
  const tokens = md.parse(source, env);
  const headings = extractHeadings(tokens);

  let html = md.renderer.render(tokens, md.options, env);
  html = wrapHeadingsForFold(html);
  html = wrapCodeBlocksForCopy(html);

  let frontmatter = null;
  if (frontmatterRaw != null) {
    try {
      const parsed = yaml.load(frontmatterRaw, { schema: yaml.JSON_SCHEMA });
      // js-yaml returns null for empty docs; treat as no frontmatter.
      frontmatter = (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) {
      frontmatter = { frontmatter: 'unparseable' };
    }
  }

  return { html, frontmatter, headings };
}

export { getShikiTheme };

// IIFE assignment for the SPA runtime. Tests import the named exports
// above directly; the SPA reads `window.mdPipeline`.
if (typeof window !== 'undefined') {
  window.mdPipeline = { renderMarkdownDoc, getShikiTheme };
}
